/**
 * Plays the frame's sim events. What an event sounds like is `eventMap.ts`'s business; this file
 * only finds WHERE it happened (events carry ids as often as coordinates), tells the score and
 * the loops about the few events they care about, and spots cook-offs: an explosion at the exact
 * spot of a prop destroyed moments ago is its fuel or ammunition going up, and sounds like it.
 */

import type { MissionDef, SimEvent, World } from '../../sim/types';
import { dogSound } from '../dogVoice';
import { placementFor, soundIdFor } from '../eventMap';
import type { Occlusion } from '../occlusion';
import { barkRate, type SfxId } from '../sfx';
import type { Graph } from './graph';
import type { Loops } from './loops';
import type { Score } from './score';
import type { Voices } from './voices';

/** Reinforcements are announced from up the map, off screen: this far north of the listener. */
const NORTH_OFFSET = 45;
const COOK_OFF_WINDOW = 2;
const RECENT = 8;

export class EventSounds {
  private readonly propAt = new Map<number, { x: number; z: number }>();
  /** Ring of recently destroyed props: x, z, audio-clock time. */
  private readonly recent = new Float64Array(RECENT * 3).fill(-1e9);
  private recentAt = 0;
  occlusion: Occlusion | null = null;

  constructor(
    private readonly graph: Graph,
    private readonly voices: Voices,
    private readonly score: Score,
    private readonly loops: Loops,
  ) {}

  begin(def: MissionDef): void {
    this.propAt.clear();
    for (const p of def.props) this.propAt.set(p.id, { x: p.x, z: p.z });
    this.recent.fill(-1e9);
  }

  private isCookOff(x: number, z: number): boolean {
    const now = this.graph.now;
    for (let i = 0; i < RECENT; i++) {
      const at = this.recent[i * 3 + 2] ?? -1e9;
      if (now - at > COOK_OFF_WINDOW) continue;
      const dx = (this.recent[i * 3] ?? 0) - x;
      const dz = (this.recent[i * 3 + 1] ?? 0) - z;
      if (Math.abs(dx) < 0.01 && Math.abs(dz) < 0.01) return true;
    }
    return false;
  }

  private find(world: World, id: number, captivesFirst: boolean): { x: number; z: number } | null {
    if (captivesFirst) for (const c of world.objective.captives) if (c.id === id) return c;
    for (const enemy of world.enemies) if (enemy.id === id) return enemy;
    return null;
  }

  private react(e: SimEvent, world: World): void {
    if (e.type === 'propDestroyed') {
      const at = this.propAt.get(e.id);
      if (at) {
        const i = this.recentAt++ % RECENT;
        this.recent[i * 3] = at.x;
        this.recent[i * 3 + 1] = at.z;
        this.recent[i * 3 + 2] = this.graph.now;
      }
      this.occlusion?.destroy(e.id);
      this.loops.propDestroyed(e.id);
    } else if (e.type === 'siren') this.loops.sirenStarted();
    else if (e.type === 'jump') this.score.jumped();
    else if (e.type === 'landed' && e.hazard !== 'badlands') this.score.touchdown();
    else if (e.type === 'ended') this.score.ended(e.outcome, world.objective.complete);
  }

  handle(events: readonly SimEvent[], world: World, lx: number, lz: number): void {
    for (const e of events) {
      this.react(e, world);
      let id: SfxId | null = soundIdFor(e);
      if (!id) continue;
      if (e.type === 'explosion' && this.isCookOff(e.x, e.z)) id = 'cookOff';
      else id = dogSound(e, id, world.enemies);
      const rate = e.type === 'bark' ? barkRate(e.id) : 1;

      let x = world.ranger.x;
      let z = world.ranger.z;
      switch (placementFor(e.type)) {
        case 'ui':
          this.voices.playUi(id, 1, rate);
          continue;
        case 'north':
          x = lx;
          z = lz - NORTH_OFFSET;
          break;
        case 'event':
          if ('x' in e) {
            x = e.x;
            z = e.z;
          }
          break;
        case 'prop': {
          const at = 'id' in e ? this.propAt.get(e.id) : undefined;
          if (!at) continue;
          x = at.x;
          z = at.z;
          break;
        }
        case 'pod': {
          const pod = 'index' in e ? world.insertion.pods[e.index] : undefined;
          if (pod) {
            x = pod.x;
            z = pod.z;
          }
          break;
        }
        case 'enemy':
        case 'captive': {
          // A freed prisoner is a captive id, but an officer who surrenders is still an enemy id:
          // captive events look among the captives first and fall back to the garrison.
          const who = 'id' in e ? this.find(world, e.id, placementFor(e.type) === 'captive') : null;
          if (who) {
            x = who.x;
            z = who.z;
          }
          break;
        }
        case 'ranger':
          break;
      }
      this.voices.playAt(id, x, z, 1, rate);
    }
  }
}
